import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { devisKeywords } from '../topKeywords';

type DevisFormValues = {
  nom: string;
  email: string;
  telephone: string;
  ville: string;
  typeLogement: string;
  surface: string;
  situation: string;
};

const schema = yup.object({
  nom: yup.string().required('Nom obligatoire'),
  email: yup.string().email('Email invalide').required('Email obligatoire'),
  telephone: yup.string().required('Téléphone obligatoire'),
  ville: yup.string().required('Ville obligatoire'),
  typeLogement: yup.string().required('Type de logement obligatoire'),
  surface: yup.string().required('Surface approximative obligatoire'),
  situation: yup.string().required('Merci de décrire la situation'),
});

export const Devis: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
    reset,
  } = useForm<DevisFormValues>({
    resolver: yupResolver(schema),
  });

  const onSubmit = (values: DevisFormValues) => {
    const messageLines = [
      'Bonjour, je souhaite un devis pour un nettoyage logement insalubre.',
      '',
      `Nom : ${values.nom}`,
      `Email : ${values.email}`,
      `Téléphone : ${values.telephone}`,
      `Ville : ${values.ville}`,
      `Type de logement : ${values.typeLogement}`,
      `Surface approximative : ${values.surface}`,
      '',
      'Description de la situation :',
      values.situation,
    ];

    const message = encodeURIComponent(messageLines.join('\n'));
    const whatsappUrl = `https://wa.me/33745171331?text=${message}`;

    window.open(whatsappUrl, '_blank');
    reset();
  };

  return (
    <div className="bg-slate-50">
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-10 lg:py-14">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">
            Devis gratuit · réponse sous 24h
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
            Devis nettoyage logement insalubre, Diogène ou nettoyage extrême 06 / 83
          </h1>
          <p className="text-sm text-slate-600 mb-3">
            Remplissez ce formulaire pour recevoir un{' '}
            <strong>devis personnalisé</strong> pour votre besoin de{' '}
            <strong>nettoyage logement insalubre</strong> dans les Alpes Maritimes ou le Var.
          </p>
          <p className="text-xs text-slate-500 mb-1">
            Exemples de demandes fréquentes :{' '}
            <em>{devisKeywords.slice(0, 2).join(', ')}</em>
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 py-8 lg:py-12">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Nom et prénom *
              </label>
              <input
                {...register('nom')}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {errors.nom && (
                <p className="mt-1 text-[11px] text-red-600">{errors.nom.message}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                {...register('email')}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {errors.email && (
                <p className="mt-1 text-[11px] text-red-600">{errors.email.message}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Téléphone *
              </label>
              <input
                {...register('telephone')}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {errors.telephone && (
                <p className="mt-1 text-[11px] text-red-600">{errors.telephone.message}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Ville d’intervention *
              </label>
              <input
                {...register('ville')}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {errors.ville && (
                <p className="mt-1 text-[11px] text-red-600">{errors.ville.message}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Surface approximative (m²) *
              </label>
              <input
                {...register('surface')}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {errors.surface && (
                <p className="mt-1 text-[11px] text-red-600">{errors.surface.message}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Type de logement *
              </label>
              <input
                {...register('typeLogement')}
                placeholder="Studio, T2, maison, etc."
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {errors.typeLogement && (
                <p className="mt-1 text-[11px] text-red-600">
                  {errors.typeLogement.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Situation (quelques lignes) *
            </label>
            <textarea
              {...register('situation')}
              rows={5}
              placeholder="Logement très sale, Diogène, squat, présence de nuisibles, odeurs fortes, etc."
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {errors.situation && (
              <p className="mt-1 text-[11px] text-red-600">{errors.situation.message}</p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              type="submit"
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-800 transition-colors"
            >
              Envoyer ma demande de devis
            </button>
            {isSubmitSuccessful && (
              <p className="text-[11px] text-emerald-600">
                Votre message WhatsApp s’est ouvert dans un nouvel onglet.
              </p>
            )}
          </div>
        </form>
      </section>
    </div>
  );
};
